<script setup lang="ts">
    import RerunStudy from '@/views/admin/RerunStudy.vue';
    import type { ApiCase } from '@/api/case';
    import { type ApiStudy, studyStateFromUrl } from '@/api/study/study';
    import { computed } from 'vue';
    import { DateTime } from 'luxon';
    import GenerateZipFile from '@/views/admin/GenerateZipFile.vue';

    const props = defineProps<{ study: ApiStudy; case: ApiCase }>();

    const isActive = computed(() => {
        return props.study.self === props.case?.study;
    });

    type _StudyState = 'new' | 'uploading' | 'processing' | 'completed' | 'error' | 'unknown';

    const studyState = computed<_StudyState>(() => {
        switch (studyStateFromUrl(props.study.state)) {
            case 'new':
                return 'new';
            case 'uploading':
                return 'uploading';
            case 'processing':
            case 'catstack':
            case 'seg-processing':
            case 'segmented':
            case 'post-processing':
                return 'processing';
            case 'completed':
                return 'completed';
            case 'error':
                return 'error';
            default:
                return 'unknown'
        }
    });

    const stateIcon = computed(() => {
        const map = {
            'new': 'mdi-timer-star',
            'uploading': 'mdi-timer-plus',
            'processing': 'mdi-timer-sync',
            'completed': 'mdi-timer-check',
            'error': 'mdi-timer-alert',
            'unknown': 'mdi-help-circle'
        } as Record<_StudyState, string>;
        return map[studyState.value];
    });

    const stateIconColor = computed(() => {
        const map = {
            'new': '#eebb00',
            'uploading': '#ec9900',
            'processing': '#f47920',
            'completed': 'success',
            'error': 'error',
            'unknown': 'grey',
        } as Record<_StudyState, string>;
        return map[studyState.value];
    });

    const created = computed(() => DateTime.fromISO(props.study.created).toHTTP());
    const updated = computed(() => DateTime.fromISO(props.study.updated).toHTTP());
</script>

<template>
    <v-expansion-panel>
        <v-expansion-panel-title>
            <div class="d-flex align-center ga-3">
                <span class="text-heading-h4">Study {{ study.id.study }}</span>
                <v-icon v-tooltip="studyState" :icon="stateIcon" :color="stateIconColor"/>
                <v-icon
                    v-show="isActive"
                    v-tooltip="'Current study'"
                    icon="mdi-star"
                    :color="stateIconColor"
                />
                <RerunStudy v-if="isActive" :study="study" operation="models" />
                <RerunStudy v-if="isActive" :study="study" operation="files" />
                <GenerateZipFile v-if="isActive" :study="study" operation="zip-files" />
            </div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
            <div class="property-grid">
                <label>name:</label>
                <span class="study-name">{{ study.name }}</span>

                <label>link:</label>
                <a :href="study.self" target="_blank"> {{ study.self }} </a>

                <label>state:</label>
                <span> {{ studyState }} </span>

                <label>created:</label>
                <span>{{ created }}</span>

                <label>updated:</label>
                <span>{{ updated }}</span>

                <label>series:</label>
                <span>{{ study.series }}</span>

                <label>file-count:</label>
                <span>{{ study.fileCount }}</span>

                <label>files:</label>
                <a :href="study.files" target="_blank"> {{ study.files }} </a>

                <label>zip-files:</label>
                <a :href="study.zipFiles" target="_blank"> {{ study.zipFiles }} </a>

                <label>cat-stacks:</label>
                <a :href="study.catStacks" target="_blank"> {{ study.catStacks }} </a>

                <label>models:</label>
                <a :href="study.models" target="_blank"> {{ study.models }} </a>

                <label>fitting:</label>
                <a :href="study?.fitting" target="_blank"> {{ study?.fitting }} </a>

                <label>landmarks:</label>
                <a :href="study?.landmarks" target="_blank"> {{ study?.landmarks }} </a>

                <label>measurements:</label>
                <a :href="study?.measurements" target="_blank"> {{ study?.measurements }} </a>
            </div>
        </v-expansion-panel-text>
    </v-expansion-panel>
</template>

<style scoped lang="scss">
    .property-grid {
        display: grid;
        grid-template-columns: min-content auto;
        column-gap: 1rem;
    }

    .property-grid > label {
        font-weight: bold;
    }

    .study-name {
        display: inline-block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: pre;
    }

    a {
        color: rgb(var(--v-theme-primary));
    }
</style>
